<template>
  <div class="home-page pa-3">
    <!-- 顶部月份导航与快捷切换 -->
    <v-card variant="flat" border class="pa-3 mb-3 rounded-xl bg-white">
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2" size="large">mdi-calendar-month-outline</v-icon>
          <div>
            <div class="text-caption text-grey">统计月份</div>
            <div class="text-subtitle-1 font-weight-bold text-grey-darken-3">
              {{ formatMonthTitle(record_form.date) }}
            </div>
          </div>
        </div>

        <div class="d-flex align-center">
          <v-btn icon="mdi-chevron-left" variant="text" size="small" @click="prevMonth"></v-btn>
          <v-btn
            v-if="!isCurrentMonth"
            size="x-small"
            variant="tonal"
            color="primary"
            class="mx-1 font-weight-bold"
            @click="resetToCurrentMonth"
          >
            本月
          </v-btn>
          <v-btn icon="mdi-chevron-right" variant="text" size="small" @click="nextMonth"></v-btn>
          <!-- 月份选择弹窗 -->
          <v-dialog v-model="modal" persistent max-width="320">
            <template v-slot:activator="{ props }">
              <v-btn icon="mdi-calendar-search" variant="text" size="small" color="primary" v-bind="props"></v-btn>
            </template>
            <v-card class="rounded-xl pa-3">
              <v-card-title class="text-subtitle-1 font-weight-bold pa-2">选择月份</v-card-title>
              <v-card-text class="pa-2">
                <v-text-field
                  v-model="record_form.date"
                  type="month"
                  label="月份"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                ></v-text-field>
              </v-card-text>
              <v-card-actions class="justify-end pa-2">
                <v-btn variant="text" @click="modal = false">取消</v-btn>
                <v-btn color="primary" variant="flat" class="rounded-pill px-4" @click="confirmMonthSelect">
                  确定
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </div>
    </v-card>

    <!-- 课时核心 KPI 统计面板 (4+4 完美对称网格) -->
    <v-card variant="flat" border class="pa-4 mb-3 rounded-xl bg-white">
      <div class="d-flex align-center justify-space-between mb-3">
        <span class="text-caption text-grey font-weight-medium">本月课时总览</span>
        <v-chip size="small" color="primary" variant="flat" class="font-weight-bold">
          总计 {{ total }} 节
        </v-chip>
      </div>

      <!-- 7 项课时分类统计网格 (第一行3项个训/集体/督导，第二行4项20M/30M/辅课/影子) -->
      <v-row dense>
        <v-col :cols="stat.cols" v-for="stat in statCards" :key="stat.label">
          <div
            class="stat-box pa-2 rounded-lg text-center"
            :style="{ borderLeft: `3px solid ${stat.value > 0 ? stat.color : '#e2e8f0'}` }"
          >
            <div class="text-caption text-grey-darken-1 mb-1">{{ stat.label }}</div>
            <div
              class="text-h6 font-weight-bold"
              :style="{ color: stat.value > 0 ? stat.color : '#94a3b8' }"
            >
              {{ stat.value }}
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- 教师课时明细表格 -->
    <v-card variant="flat" border class="pa-3 mb-10 rounded-xl bg-white">
      <div class="d-flex align-center justify-space-between mb-3 px-1">
        <div class="d-flex align-center text-subtitle-2 font-weight-bold text-grey-darken-3">
          <v-icon icon="mdi-account-group-outline" size="small" class="mr-1" color="primary" />
          教师课时明细
        </div>
        <span class="text-caption text-grey">共 {{ teacher_detail.length }} 人</span>
      </div>

      <div v-if="teacher_detail.length === 0" class="text-center py-6 text-grey">
        <v-icon icon="mdi-clipboard-text-outline" size="large" class="mb-1" />
        <div class="text-caption">本月暂无统计记录</div>
      </div>

      <v-table v-else density="compact" hover class="detail-table">
        <thead>
          <tr>
            <th class="text-left font-weight-bold">教师</th>
            <th class="text-center font-weight-bold text-primary">合计</th>
            <th class="text-center font-weight-bold">个训</th>
            <th class="text-center font-weight-bold">集体</th>
            <th class="text-center font-weight-bold">督导</th>
            <th class="text-center font-weight-bold">20M</th>
            <th class="text-center font-weight-bold">30M</th>
            <th class="text-center font-weight-bold">辅课</th>
            <th class="text-center font-weight-bold">影子</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in teacher_detail" :key="item.name">
            <td class="font-weight-bold text-left" style="white-space: nowrap">
              {{ item.name }}
            </td>
            <td class="text-center font-weight-bold">
              <span :class="calcTeacherTotal(item) > 0 ? 'num-total' : 'num-zero'">
                {{ calcTeacherTotal(item) }}
              </span>
            </td>
            <td class="text-center">
              <span :class="(item.single || 0) > 0 ? 'num-active' : 'num-zero'">
                {{ item.single || 0 }}
              </span>
            </td>
            <td class="text-center">
              <span :class="(item.group || 0) > 0 ? 'num-active' : 'num-zero'">
                {{ item.group || 0 }}
              </span>
            </td>
            <td class="text-center">
              <span :class="(item.supervision || 0) > 0 ? 'num-active' : 'num-zero'">
                {{ item.supervision || 0 }}
              </span>
            </td>
            <td class="text-center">
              <span :class="(item.connect || 0) > 0 ? 'num-active' : 'num-zero'">
                {{ item.connect || 0 }}
              </span>
            </td>
            <td class="text-center">
              <span :class="(item.halfHourLesson || 0) > 0 ? 'num-active' : 'num-zero'">
                {{ item.halfHourLesson || 0 }}
              </span>
            </td>
            <td class="text-center">
              <span :class="(item.subsidiary || 0) > 0 ? 'num-active' : 'num-zero'">
                {{ item.subsidiary || 0 }}
              </span>
            </td>
            <td class="text-center">
              <span :class="(item.shadow || 0) > 0 ? 'num-active' : 'num-zero'">
                {{ item.shadow || 0 }}
              </span>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
  </div>
</template>

<script>
import { getRecordByUserId } from "@/api";
import moment from "moment";

export default {
  name: "Home",
  data: () => ({
    total: 0,
    single: 0,
    group: 0,
    shadow: 0,
    connect: 0,
    subsidiary: 0,
    halfHourLesson: 0,
    supervision: 0,
    record_form: {
      date: moment().format("YYYY-MM"),
    },
    modal: false,
    teacher_detail: [],
  }),

  computed: {
    isCurrentMonth() {
      return this.record_form.date === moment().format("YYYY-MM");
    },
    // 7项课程 (第一行3项个训/集体/督导；第二行4项20MIN/30MIN/辅课/影子)
    statCards() {
      return [
        { label: "个训", value: this.single, color: "#4CAF50", cols: 4 },
        { label: "集体", value: this.group, color: "#4DB6AC", cols: 4 },
        { label: "督导", value: this.supervision, color: "#673AB7", cols: 4 },
        { label: "20MIN", value: this.connect, color: "#607D8B", cols: 3 },
        { label: "30MIN", value: this.halfHourLesson, color: "#795548", cols: 3 },
        { label: "辅课", value: this.subsidiary, color: "#FB8C00", cols: 3 },
        { label: "影子", value: this.shadow, color: "#3F51B5", cols: 3 },
      ];
    },
  },

  mounted() {
    this.listStudyRecord();
  },

  methods: {
    formatMonthTitle(dateStr) {
      if (!dateStr) return "";
      return moment(dateStr).format("YYYY年 MM月");
    },

    prevMonth() {
      this.record_form.date = moment(this.record_form.date)
        .subtract(1, "months")
        .format("YYYY-MM");
      this.listStudyRecord("search");
    },

    nextMonth() {
      this.record_form.date = moment(this.record_form.date)
        .add(1, "months")
        .format("YYYY-MM");
      this.listStudyRecord("search");
    },

    resetToCurrentMonth() {
      this.record_form.date = moment().format("YYYY-MM");
      this.listStudyRecord("search");
    },

    confirmMonthSelect() {
      this.modal = false;
      this.listStudyRecord("search");
    },

    calcTeacherTotal(item) {
      return (
        (item.single || 0) +
        (item.group || 0) +
        (item.supervision || 0) +
        (item.connect || 0) +
        (item.halfHourLesson || 0) +
        (item.subsidiary || 0) +
        (item.shadow || 0)
      );
    },

    listStudyRecord(param) {
      let month =
        param === "search"
          ? moment(this.record_form.date)
          : moment();

      let startTime = month.startOf("month").format("YYYY-MM-DD HH:mm:ss.SSS");
      let endTime = month.endOf("month").format("YYYY-MM-DD HH:mm:ss.SSS");

      getRecordByUserId({
        user_id: localStorage.getItem("id"),
        start_at: startTime,
        end_at: endTime,
      }).then((res) => {
        const detail = res.data?.data?.detail || {};
        this.group = detail.group || 0;
        this.single = detail.single || 0;
        this.total = detail.total || 0;
        this.shadow = detail.shadow || 0;
        this.connect = detail.connect || 0;
        this.halfHourLesson = detail.halfHourLesson || 0;
        this.supervision = detail.supervision || 0;
        this.subsidiary = detail.subsidiary || 0;
        this.teacher_detail = detail.user_detail || [];
      });
    },
  },
};
</script>

<style scoped>
.home-page {
  max-width: 600px;
  margin: 0 auto;
}

.stat-box {
  background-color: #f8fafc;
}

.detail-table {
  font-size: 12px;
  width: 100%;
}

.detail-table th {
  background-color: #f1f5f9;
  color: #475569;
  padding: 7px 2px !important;
  font-size: 11.5px;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

.detail-table td {
  padding: 6px 2px !important;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}

/* 首列“教师”吸附固定 (Sticky Left) */
.detail-table th:first-child,
.detail-table td:first-child {
  position: sticky;
  left: 0;
  border-right: 1px solid #e2e8f0;
  box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.08);
}

.detail-table th:first-child {
  z-index: 3;
  background-color: #f1f5f9;
  padding-left: 6px !important;
  padding-right: 4px !important;
}

.detail-table td:first-child {
  z-index: 2;
  background-color: #ffffff;
  padding-left: 6px !important;
  padding-right: 4px !important;
}

.detail-table tbody tr:hover td:first-child {
  background-color: #f8fafc;
}

.num-zero {
  color: #94a3b8;
  font-weight: 400;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.num-active {
  color: #0f172a;
  font-weight: 700;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.num-total {
  color: #1867c0;
  font-weight: 700;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
</style>
